/**
 * MangoPay validation error transformation utilities
 *
 * These utilities help convert MangoPay API validation errors into a format
 * compatible with Formik's error handling, and provide field mapping between
 * MangoPay field paths and form field names.
 */

/**
 * Convert flat error field paths to nested error objects for Formik
 *
 * @example
 * // Input: [{field: "LocalBankTransfer.EUR.IBAN", message: "Invalid IBAN"}]
 * // Output: {LocalBankTransfer: {EUR: {IBAN: "Invalid IBAN"}}}
 */
export function transformFlatErrorsToNested(
  errors: Array<{ field: string; message: string }>
): Record<string, unknown> {
  const nestedErrors: Record<string, unknown> = {};

  errors.forEach((error) => {
    const parts = error.field.split('.');
    let current = nestedErrors as Record<string, unknown>;

    // Navigate/create the nested structure
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      if (!current[part]) {
        current[part] = {};
      }
      current = current[part] as Record<string, unknown>;
    }

    // Set the error message at the final level
    const lastPart = parts[parts.length - 1];
    current[lastPart] = error.message;
  });

  return nestedErrors;
}

/**
 * Transform validation error response to have nested error structure
 */
export function transformValidationError(error: {
  errors?: Array<{ field: string; message: string }>;
  [key: string]: unknown;
}): {
  errors?: Array<{ field: string; message: string }>;
  nestedErrors?: Record<string, unknown>;
  [key: string]: unknown;
} {
  if (error?.errors && Array.isArray(error.errors)) {
    // Create a new error object with transformed errors
    const transformedError = {
      ...error,
      errors: error.errors, // Keep original for debugging
      nestedErrors: transformFlatErrorsToNested(error.errors),
    };
    return transformedError;
  }
  return error;
}

/**
 * Filter errors to only include errors for fields that have been touched by the user
 * This prevents showing stale validation errors for untouched fields
 *
 * @param errors - Nested error object from Formik
 * @param touched - Nested touched object from Formik
 * @returns Filtered error object containing only errors for touched fields
 */
export function filterErrorsByTouched(
  errors: Record<string, unknown>,
  touched: Record<string, unknown>
): Record<string, unknown> {
  const filtered: Record<string, unknown> = {};

  const walkErrors = (
    errorObj: unknown,
    touchedObj: unknown,
    targetObj: Record<string, unknown>
  ) => {
    if (!errorObj || typeof errorObj !== 'object') {
      return;
    }

    Object.entries(errorObj as Record<string, unknown>).forEach(
      ([key, value]) => {
        if (typeof value === 'string') {
          // Leaf error - check if field is touched
          if (touchedObj && (touchedObj as Record<string, unknown>)[key]) {
            targetObj[key] = value;
          }
        } else if (typeof value === 'object' && value !== null) {
          // Nested errors - check if nested object has any touched fields
          const nestedTouched =
            touchedObj && (touchedObj as Record<string, unknown>)[key];
          if (nestedTouched && typeof nestedTouched === 'object') {
            const nestedTarget: Record<string, unknown> = {};
            walkErrors(value, nestedTouched, nestedTarget);

            // Only add nested object if it has any errors
            if (Object.keys(nestedTarget).length > 0) {
              targetObj[key] = nestedTarget;
            }
          }
        }
      }
    );
  };

  walkErrors(errors, touched, filtered);
  return filtered;
}

/**
 * Mapping from MangoPay API field paths to family member form field names
 *
 * MangoPay uses nested structures like:
 * - IndividualRecipient.FirstName
 * - IndividualRecipient.Address.AddressLine1
 * - LocalBankTransfer.EUR.IBAN
 *
 * Family member forms use flat field names like:
 * - firstName, lastName, iban, city, etc.
 */
const MANGOPAY_TO_FORM_FIELD_MAP: Record<string, string> = {
  // Individual recipient fields
  'IndividualRecipient.FirstName': 'firstName',
  'IndividualRecipient.LastName': 'lastName',
  'IndividualRecipient.Address.AddressLine1': 'addressLine1',
  'IndividualRecipient.Address.AddressLine2': 'addressLine2',
  'IndividualRecipient.Address.City': 'city',
  'IndividualRecipient.Address.PostalCode': 'postalCode',
  'IndividualRecipient.Address.Country': 'country',
  'IndividualRecipient.Address.Region': 'region',

  // Business recipient fields (for service providers)
  'BusinessRecipient.BusinessName': 'businessName',
  'BusinessRecipient.Address.AddressLine1': 'addressLine1',
  'BusinessRecipient.Address.AddressLine2': 'addressLine2',
  'BusinessRecipient.Address.City': 'city',
  'BusinessRecipient.Address.PostalCode': 'postalCode',
  'BusinessRecipient.Address.Country': 'country',
  'BusinessRecipient.Address.Region': 'region',

  // Bank transfer fields
  'LocalBankTransfer.EUR.IBAN': 'iban',
  'LocalBankTransfer.EUR.BIC': 'bic',

  // Top-level fields
  DisplayName: 'displayName',
  Country: 'country',
  Tag: 'tag',
};

/**
 * Map a MangoPay field path to the corresponding form field name
 *
 * @param mangopayField - The field path from MangoPay API (e.g., "IndividualRecipient.FirstName")
 * @returns The corresponding form field name (e.g., "firstName") or the original if no mapping exists
 *
 * @example
 * mapMangopayFieldToFormField("IndividualRecipient.FirstName") // "firstName"
 * mapMangopayFieldToFormField("LocalBankTransfer.EUR.IBAN") // "iban"
 * mapMangopayFieldToFormField("UnknownField") // "UnknownField"
 */
export function mapMangopayFieldToFormField(mangopayField: string): string {
  return MANGOPAY_TO_FORM_FIELD_MAP[mangopayField] ?? mangopayField;
}

/**
 * Transform MangoPay validation errors to use form field names
 *
 * @param errors - Array of errors from MangoPay API with MangoPay field paths
 * @returns Array of errors with form field names
 *
 * @example
 * const mangopayErrors = [
 *   { field: "IndividualRecipient.FirstName", message: "REQUIRED" },
 *   { field: "LocalBankTransfer.EUR.IBAN", message: "INVALID_IBAN" }
 * ];
 * const formErrors = mapMangopayErrorsToFormFields(mangopayErrors);
 * // [
 * //   { field: "firstName", message: "REQUIRED" },
 * //   { field: "iban", message: "INVALID_IBAN" }
 * // ]
 */
export function mapMangopayErrorsToFormFields(
  errors: Array<{ field: string; message: string }>
): Array<{ field: string; message: string }> {
  return errors.map((error) => ({
    field: mapMangopayFieldToFormField(error.field),
    message: error.message,
  }));
}

/**
 * Process MangoPay validation errors for use with Formik forms
 *
 * This combines field mapping and nesting transformation into a single function
 * that produces a Formik-compatible error object with form field names.
 *
 * @param errors - Array of errors from MangoPay API
 * @returns Nested error object suitable for Formik's setErrors
 *
 * @example
 * const mangopayErrors = [
 *   { field: "IndividualRecipient.FirstName", message: "Name is required" },
 *   { field: "LocalBankTransfer.EUR.IBAN", message: "Invalid IBAN format" }
 * ];
 * const formikErrors = processMangopayValidationErrors(mangopayErrors);
 * // { firstName: "Name is required", iban: "Invalid IBAN format" }
 */
export function processMangopayValidationErrors(
  errors: Array<{ field: string; message: string }>
): Record<string, unknown> {
  const mappedErrors = mapMangopayErrorsToFormFields(errors);
  return transformFlatErrorsToNested(mappedErrors);
}
